import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";

// Opt-in product tour. Never auto-launches — it only runs when the athlete
// asks for it (restartTour() from the Nutrition Guide). Highlights elements
// by `data-tour-step="slug"`, leaves the target genuinely clickable, and
// tracks it in viewport coordinates so the spotlight can't drift on scroll.
const LS_RUN = "spc_tour_run";

const STEPS = [
  { slug: "home-build-plan", title: "Build your week", body: "This builds your weekly fuelling plan — meals, macros and shopping list. Quick Build takes about a minute." },
  { slug: "home-open-tracker", title: "Log your food", body: "Tracker is where you live day-to-day: log every meal and snack against the plan, plus weight and progress photos." },
  { slug: "profile-menu", title: "Coach chat + settings", body: "Message your coach, switch units, or change your weight-tracking cadence from here." },
];

const getTarget = (slug) => (slug ? document.querySelector(`[data-tour-step="${slug}"]`) : null);

export default function ProductTour() {
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [blocked, setBlocked] = useState(false);

  // Launch only on explicit request, and only once the first target exists.
  useEffect(() => {
    if (localStorage.getItem(LS_RUN) !== "1") return;
    let attempts = 0;
    const tick = setInterval(() => {
      attempts += 1;
      if (getTarget(STEPS[0].slug)) {
        clearInterval(tick);
        localStorage.removeItem(LS_RUN);
        setActive(true);
      } else if (attempts > 20) {
        clearInterval(tick);
        localStorage.removeItem(LS_RUN);
      }
    }, 300);
    return () => clearInterval(tick);
  }, []);

  const step = STEPS[idx];
  const dismiss = useCallback(() => { setActive(false); setRect(null); }, []);
  const next = useCallback(() => {
    setIdx((i) => {
      // Skip any step whose target isn't on this screen.
      for (let n = i + 1; n < STEPS.length; n += 1) if (getTarget(STEPS[n].slug)) return n;
      setActive(false);
      return i;
    });
  }, []);
  const back = () => setIdx((i) => {
    for (let n = i - 1; n >= 0; n -= 1) if (getTarget(STEPS[n].slug)) return n;
    return i;
  });

  // Viewport-relative measurement, re-run on scroll and resize so the
  // spotlight stays welded to the element.
  useLayoutEffect(() => {
    if (!active) return;
    const el = getTarget(step?.slug);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    let raf = 0;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    measure();
    const settle = setTimeout(measure, 450);
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, true);
    // Tapping the highlighted control does its real job and moves the tour on.
    const onTargetClick = () => setTimeout(next, 120);
    el.addEventListener("click", onTargetClick);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      clearTimeout(0);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
      el.removeEventListener("click", onTargetClick);
    };
  }, [active, idx, step?.slug, next]);

  // Stand down while any dialog is open so we never cover a modal.
  useEffect(() => {
    if (!active) return;
    const check = () => setBlocked(!!document.querySelector('[role="dialog"]'));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [active]);

  if (!active || blocked || !rect) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const pad = 6;
  const hole = { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 };
  const shade = "rgba(0,0,0,0.72)";

  const width = Math.min(320, vw - 32);
  const estHeight = 190;
  const roomBelow = vh - (hole.top + hole.height);
  const placeBelow = roomBelow > estHeight + 16;
  const top = placeBelow
    ? Math.min(hole.top + hole.height + 12, vh - estHeight - 12)
    : Math.max(12, hole.top - estHeight - 12);
  const left = Math.max(16, Math.min(vw - width - 16, hole.left + hole.width / 2 - width / 2));

  return (
    <div data-testid="product-tour" className="fixed inset-0 z-[9998]">
      {/* Four panels frame the target instead of covering it, so the
          highlighted control stays tappable. Backdrop taps do nothing. */}
      <div className="absolute" style={{ top: 0, left: 0, right: 0, height: Math.max(0, hole.top), background: shade }} />
      <div className="absolute" style={{ top: hole.top + hole.height, left: 0, right: 0, bottom: 0, background: shade }} />
      <div className="absolute" style={{ top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height, background: shade }} />
      <div className="absolute" style={{ top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height, background: shade }} />
      <div className="absolute pointer-events-none"
        style={{
          top: hole.top, left: hole.left, width: hole.width, height: hole.height,
          boxShadow: "0 0 0 2px #E2E30A, 0 0 24px -4px #E2E30A",
          transition: "top 220ms ease, left 220ms ease, width 220ms ease, height 220ms ease",
        }} />

      <div className="absolute bg-[#141414] border border-[#E2E30A]/50 p-4 shadow-2xl"
        data-testid="product-tour-tooltip" style={{ top, left, width }}>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-3.5 w-3.5 text-[#E2E30A]" />
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#E2E30A]">{idx + 1} / {STEPS.length} · Quick tour</p>
          <button data-testid="product-tour-close" onClick={dismiss} aria-label="Close tour"
            className="ml-auto text-zinc-400 hover:text-white">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="font-display text-lg leading-tight text-white">{step.title}</p>
        <p className="text-xs text-zinc-300 leading-relaxed mt-2">{step.body}</p>
        <div className="flex items-center justify-between mt-3">
          <button data-testid="product-tour-skip" onClick={dismiss}
            className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-white">Skip</button>
          <div className="flex gap-1.5">
            {idx > 0 && (
              <button data-testid="product-tour-back" onClick={back}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2.5 py-1 border border-white/15 text-zinc-300 hover:text-white hover:border-white/40">
                <ChevronLeft className="h-3 w-3" /> Back
              </button>
            )}
            <button data-testid="product-tour-next" onClick={idx >= STEPS.length - 1 ? dismiss : next}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#E2E30A] text-black px-3 py-1 hover:bg-[#c8c908]">
              {idx >= STEPS.length - 1 ? "Done" : <>Next <ChevronRight className="h-3 w-3" /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Called from the Nutrition Guide's "Take the quick tour" link.
export function restartTour() {
  localStorage.setItem(LS_RUN, "1");
  if (window.location.pathname === "/") window.location.reload();
  else window.location.href = "/";
}
